import Swal from "sweetalert2";
import { getUserLanguage, systemErrorAlertText } from "../utilities/utils";
// import { FetchResponse } from "../types";
// import {
//   getTokensLocalStorage,
//   isTokenExpired,
//   requestAndSetTokens
// } from "../utilities/tokenHandler.util";

type RequestMethod = "GET" | "POST" | "PUT" | "DELETE";

interface FetchOptions {
  body?: object;
  headers?: HeadersInit;
}

export async function apiFetcher({
  route,
  method,
  options,
  signal,
  excludeToken
}: {
  route: string;
  method: RequestMethod;
  options?: FetchOptions;
  signal?: AbortSignal;
  excludeToken?: boolean;
}): Promise<APIFetchResponse> {
  // console.log("---apiFetcher---");
  const defaultHeaders: HeadersInit = {
    "Content-Type": "application/json"
  };

  let fetchOptions: RequestInit = {
    method: method,
    headers: {
      ...defaultHeaders,
      ...options?.headers
    }
  };

  if (options?.body && (method === "POST" || method === "PUT")) {
    // get language from local storage, i18nextLng
    const language = getUserLanguage();
    options.body = { ...options.body, lanIso: language };

    // Include the refresh token in headers if it exists and excludeToken is not true
    const refreshToken = localStorage.getItem("refreshToken");
    if (refreshToken && !excludeToken) {
      options.body = { ...options.body, refreshToken: refreshToken };
    }
    const referrerToken = localStorage.getItem("referrerToken");
    if (referrerToken) {
      options.body = { ...options.body, referrerToken: referrerToken };
    }
    fetchOptions.body = JSON.stringify(options.body);
  }
  // console.log(options?.body);

  let initialStatus: number | null = null;
  const apiLink = process.env.REACT_APP_API_URL;

  if (!apiLink) {
    console.error("API URL is not set");
    throw new Error("API URL is not set");
  }

  const fullRoute = `${apiLink}${route}`;

  // console.log(fullRoute, "fullRoute");
  try {
    const fetchOptionsWithSignal = { ...fetchOptions, signal };

    const response = await fetch(fullRoute, fetchOptionsWithSignal);
    // console.log(response, "response");
    initialStatus = response.status; // Store the initial status code
    // console.log(initialStatus, "initialStatus");
    if (initialStatus >= 500) {
      // console.log("Server error");
      throw new Error("Server error");
    } else if (initialStatus === 400) {
      // Frontend not sending correct data format
      // console.log("Data format error");
      throw new Error("Data format error");
    } else if (initialStatus === 401) {
      // Attempt to request and set one more time, if it fails, redirect to login
      console.log("HIT");
      // console.log("Auth failed");
      throw new Error("Auth failed");
    } else if (initialStatus === 403) {
      // console.log("No data found");
      throw new Error("No Permission");
    } else if (initialStatus === 404) {
      // console.log("No data found");
      throw new Error("No data found");
    } else if (initialStatus === 409) {
      // console.log("Already exists");
      throw new Error("Already exists");
    } else if (initialStatus === 406) {
      // console.log("Not acceptable");
      try {
        const data = await response.json();
        return {
          status: 406,
          data: data
        };
      } catch {
        return {
          status: 406,
          data: null
        };
      }
    } else if (initialStatus === 422) {
      throw new Error("Unprocessable Entity");
    } else if (initialStatus === 429) {
      // console.log("Too many requests");
      throw new Error("Too many requests");
    } else if (initialStatus === 423) {
      // console.log("Locked");
      throw new Error("Locked");
    } else if (initialStatus === 202) {
      // Accepted, no data returned
      return {
        status: 202,
        data: null
      };
    } else if (initialStatus === 206) {
      // Partially successful
      // Parse the response JSON
      try {
        const data = await response.json();
        return {
          status: 206,
          data: data
        };
      } catch {
        return {
          status: 206,
          data: null
        };
      }
    } else if (initialStatus === 204) {
      return {
        status: 204,
        data: null
      };
    } else if (initialStatus === 209) {
      try {
        const data = await response.json();
        return {
          status: 209,
          data: data
        };
      } catch {
        return {
          status: 209,
          data: null
        };
      }
    } else if (initialStatus === 200) {
      // console.log("Success");
      // Parse the response JSON
      const data = await response.json();
      console.log(data, "data");
      // console.log(response.status, "response.status");
      return {
        status: initialStatus, // Use the initial status code
        data: data
      };
    } else {
      // console.log("Unknown error");
      throw new Error("Unknown error");
    }
  } catch (error) {
    console.log("catch error");
    console.log(error);
    // check if the error is an AbortError
    if (error instanceof DOMException && error.name === "AbortError") {
      console.log("Request aborted");
      return {
        status: 0,
        data: null,
        error: "request_aborted"
      };
    }

    // check if there is a network error
    if (error instanceof TypeError && error.message === "Failed to fetch") {
      console.log("Network error");
      return {
        status: 0,
        data: null,
        error: "network_error"
      };
    }

    type responseIssue = {
      statusCode: number;
      errorMessage: string;
      body: FetchOptions["body"];
      fullRoute: string;
    };
    // use resIssue for debugging
    const resIssue: responseIssue = {
      fullRoute: fullRoute,
      statusCode: initialStatus || 418,
      errorMessage: (error as Error).message,
      body: options?.body
    };
    console.log(resIssue);
    const acceptableResponses = [
      500, 400, 401, 403, 404, 406, 409, 422, 429, 423, 202, 204, 206, 200
    ];
    if (initialStatus && !acceptableResponses.includes(initialStatus)) {
      Swal.fire({
        icon: "error",
        title: "Unknown response code: " + initialStatus + "!",
        html:
          `<strong class="font-extrabold">Please contact support</strong>` +
          systemErrorAlertText(),
        showConfirmButton: true,
        confirmButtonText: "Ok",
        customClass: {
          popup: "!rounded-lg",
          confirmButton:
            "!bg-secondary !rounded-full !outline-none !border-none !shadow-none"
        }
      });
    }
    console.log("error");
    console.log(error);
    console.log("initialStatus");
    console.log(initialStatus);
    if (initialStatus === 200 || initialStatus === 206) {
      await Swal.fire({
        icon: "error",
        title: "Error!",
        html:
          `<strong class="font-extrabold">Please contact support</strong><br/>Unable to parse data from server` +
          systemErrorAlertText(),
        showConfirmButton: true,
        confirmButtonText: "Ok",
        customClass: {
          popup: "!rounded-lg",
          confirmButton:
            "!bg-secondary !rounded-full !outline-none !border-none !shadow-none"
        }
      }).then(() => {
        // window.location.reload();
      });
    }

    return {
      status:
        initialStatus && acceptableResponses.includes(initialStatus)
          ? initialStatus
          : 418, // Use the initial status if in the acceptableResponses array, else 418
      data: null,
      error: (error as Error).message
    };
  }
}

// Specifically for AI API calls
export async function apiFetcherAi({
  route,
  method,
  options,
  signal
}: {
  route: string;
  method: RequestMethod;
  options?: FetchOptions;
  signal?: AbortSignal;
  excludeToken?: boolean;
}): Promise<AIChatFetchResponse> {
  // Validations
  if (!process.env.REACT_APP_AI_HEADER) {
    console.error("API AI Header is not set");
    throw new Error("API AI Header is not set");
  }

  if (!process.env.REACT_APP_AI_URL) {
    console.error("API AI URL is not set");
    throw new Error("API AI URL is not set");
  }

  if (
    !method ||
    typeof method !== "string" ||
    (method !== "POST" && method !== "GET")
  ) {
    console.error("Invalid method for AI chat as: ", method);
    throw new Error("Invalid method for AI chat");
  }

  if (!route || typeof route !== "string" || route === "") {
    console.error("Invalid route for AI chat as: ", route);
    throw new Error("Invalid route for AI chat");
  }

  let fetchOptions: RequestInit = {
    method: method,
    headers: {
      "Content-Type": "application/json",
      "GoalRush": process.env.REACT_APP_AI_HEADER
    }
  };

  const apiLink = process.env.REACT_APP_AI_URL;

  const fullRoute = `${apiLink}${route}`;

  if (options?.body && method === "POST") {
    fetchOptions.body = JSON.stringify(options.body);
  }

  try {
    const fetchOptionsWithSignal = { ...fetchOptions, signal };

    const response = await fetch(fullRoute, fetchOptionsWithSignal);
    if (response) {
      if (!response.status) {
        // not likely
        return {
          statusKey: "no_response_status",
          data: null
        };
      }
      try {
        // Testing found response status is only 200 (for errors too)
        if (response.status === 200) {
          const data = await response.json();
          if (data.response) {
            if (typeof data.response === "string") {
              if (data.response.trim() === "") {
                console.error("AI Chat server sent empty message");
                // no message
                return {
                  statusKey: "no_data",
                  data: null
                };
              } else {
                return {
                  // SUCCESS
                  statusKey: "success",
                  data: data.response
                };
              }
            } else {
              // server response is not of known type
              console.error(
                "AI Chat server response type is not known",
                data.response
              );
              return {
                statusKey: "server_response_unknown_status",
                data: null
              };
            }
          } else if (data.error) {
            console.error("AI Chat Server Error as: ", data);
            // error message
            return {
              statusKey: "error_server",
              data: null
            };
          } else {
            // server response is not of known type
            console.error(
              "AI Chat server response type is not known",
              data.response
            );
            return {
              statusKey: "server_response_unknown_status",
              data: null
            };
          }
        } else {
          // this should not happen as even non-success responses were seen to have a status
          // of 200
          console.error("AI Chat Server sent unknown status");
          return {
            statusKey: "server_response_unknown_status",
            data: null
          };
        }
      } catch (error) {
        console.log("Error parsing / handling response data:", error);
        return {
          statusKey: "error_server",
          data: null
        };
      }
    } else {
      return {
        statusKey: "no_response",
        data: null
      };
    }
  } catch (error) {
    // check if the error is an AbortError
    if (error instanceof DOMException && error.name === "AbortError") {
      console.log("Request aborted");
      return {
        statusKey: "request_aborted",
        data: null
      };
    }

    // Network Error
    if (error instanceof TypeError && error.message === "Failed to fetch") {
      console.log("Network error");
      return {
        statusKey: "error_network",
        data: null
      };
    }

    // outer error catch
    console.log("-- AI CHAT SERVICE OUTER CATCH ERROR --", error);

    // this should never be reached and indicates unknown actions took place, serves only
    // as a safeguard
    console.error(
      "AI Service safeguard response hit, unknown error or logic  execution may have occured"
    );
    return {
      statusKey: "termination_unknown_handler",
      data: null
    };
  }
}
